import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/LanguageContext';
import { DUKE_SKIN_IMAGES } from '../config/DukeSkins';
import '../styles/DukeSkinStore.css';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to get duke image URL
const getDukeImageUrl = (skinId, type) => {
  const skinImages = DUKE_SKIN_IMAGES[skinId] || DUKE_SKIN_IMAGES.default;
  return skinImages[type] || DUKE_SKIN_IMAGES.default[type];
};

// Duke skin catalog with pricing - comprehensive list
const DUKE_SKIN_CATALOG = [
  // === PREMIUM WHITE PIECES ===
  { id: 'silver', name: 'Silver', category: 'white', price: 1.99, rarity: 'rare', description: 'Elegant silver shield with metallic finish' },
  { id: 'white', name: 'White Spartan', category: 'white', price: 2.49, rarity: 'epic', description: 'Legendary Spartan warrior shield' },
  { id: 'whiteEagle', name: 'White Eagle', category: 'white', price: 2.99, rarity: 'epic', description: 'Majestic eagle emblem on pristine shield' },
  { id: 'goldCrown', name: 'Gold Crown', category: 'white', price: 3.49, rarity: 'legendary', description: 'Royal golden crown with jeweled details' },
  { id: 'platinum', name: 'Platinum', category: 'white', price: 2.99, rarity: 'epic', description: 'Premium platinum with brilliant shine' },
  { id: 'whiteGoldBase', name: 'White Gold Base', category: 'white', price: 2.49, rarity: 'epic', description: 'White shield with gold base trim' },
  { id: 'whiteMarble', name: 'White Marble Knight', category: 'white', price: 2.99, rarity: 'epic', description: 'Elegant marble texture with knight emblem' },
  { id: 'lightGold', name: 'Light Gold', category: 'white', price: 1.99, rarity: 'rare', description: 'Soft golden glow for bright battles' },
  { id: 'silverOrnate', name: 'Silver Ornate', category: 'white', price: 2.49, rarity: 'epic', description: 'Intricately decorated silver shield' },
  { id: 'colossalWhite', name: 'Colossal White', category: 'white', price: 3.99, rarity: 'legendary', description: 'Epic marble sculpted titan shield' },
  { id: 'crownWhiteOutline', name: 'Crown White Outline', category: 'white', price: 2.99, rarity: 'epic', description: 'Elegant crown with thick white border' },
  { id: 'clearWhite', name: 'Clear White', category: 'white', price: 2.49, rarity: 'epic', description: 'Crystal clear shield with white glow' },
  { id: 'silver3D', name: 'Silver 3D Premium', category: 'white', price: 3.99, rarity: 'legendary', description: '3D rendered silver shield' },
  { id: 'swordsGray', name: 'Swords Gray', category: 'white', price: 2.99, rarity: 'epic', description: 'Crossed swords on gray shield' },
  // === PREMIUM BLACK PIECES ===
  { id: 'dark', name: 'Dark Steel', category: 'black', price: 2.49, rarity: 'epic', description: 'Forged in darkness, tempered in battle' },
  { id: 'darkSteel', name: 'Dark Steel 3D', category: 'black', price: 3.49, rarity: 'legendary', description: '3D rendered dark steel shield' },
  { id: 'purple', name: 'Purple', category: 'black', price: 2.49, rarity: 'epic', description: 'Royal purple with mystical aura' },
  { id: 'purpleVelvet', name: 'Purple Velvet', category: 'black', price: 2.99, rarity: 'epic', description: 'Luxurious velvet purple finish' },
  { id: 'blue', name: 'Blue', category: 'black', price: 1.99, rarity: 'rare', description: 'Deep ocean blue with royal crest' },
  { id: 'blueCrown', name: 'Blue Crown', category: 'black', price: 2.49, rarity: 'epic', description: 'Sapphire crown on dark shield' },
  { id: 'royalBlue', name: 'Royal Blue', category: 'black', price: 2.99, rarity: 'epic', description: 'Fit for royalty, deep royal blue' },
  { id: 'navyBlue', name: 'Navy Blue', category: 'black', price: 2.49, rarity: 'epic', description: 'Dark navy with gold accents' },
  { id: 'crimson', name: 'Crimson', category: 'black', price: 2.99, rarity: 'epic', description: 'Blood red with fierce emblem' },
  { id: 'ruby', name: 'Ruby', category: 'black', price: 3.49, rarity: 'legendary', description: 'Precious ruby gemstone shine' },
  { id: 'blackCrown', name: 'Black Crown', category: 'black', price: 3.49, rarity: 'legendary', description: 'Crown of the shadow realm' },
  { id: 'blackEagle', name: 'Black Eagle', category: 'black', price: 2.99, rarity: 'epic', description: 'Dark eagle rising from shadows' },
  { id: 'blackMarble', name: 'Black Marble Knight', category: 'black', price: 2.99, rarity: 'epic', description: 'Dark marble with knight emblem' },
  { id: 'charcoal', name: 'Charcoal', category: 'black', price: 1.99, rarity: 'rare', description: 'Smoldering charcoal finish' },
  { id: 'darkGold', name: 'Dark Gold', category: 'black', price: 2.99, rarity: 'epic', description: 'Gold with dark undertones' },
  { id: 'colossalBronze', name: 'Colossal Bronze', category: 'black', price: 3.99, rarity: 'legendary', description: 'Epic bronze sculpted titan shield' },
  { id: 'crownBlackOutline', name: 'Crown Black Outline', category: 'black', price: 2.99, rarity: 'epic', description: 'Elegant crown with thick black border' },
  { id: 'clearBlack', name: 'Clear Black', category: 'black', price: 2.49, rarity: 'epic', description: 'Crystal clear with dark glow' },
  { id: 'stealthBlack3D', name: 'Stealth Black 3D', category: 'black', price: 3.99, rarity: 'legendary', description: '3D rendered stealth black shield' },
  { id: 'neonGreen3D', name: 'Neon Green 3D', category: 'black', price: 3.99, rarity: 'legendary', description: '3D shield with neon green glow' },
  { id: 'purpleNeon3D', name: 'Purple Neon 3D', category: 'black', price: 3.99, rarity: 'legendary', description: '3D shield with purple neon glow' },
  { id: 'swordsBlack', name: 'Swords Black', category: 'black', price: 2.99, rarity: 'epic', description: 'Crossed swords on black shield' },
  // === NEUTRAL (WORKS FOR BOTH) ===
  { id: 'default', name: 'Classic', category: 'neutral', price: 0, rarity: 'common', isFree: true, description: 'The original Duke shield - free for all' },
  { id: 'gold', name: 'Gold', category: 'neutral', price: 2.99, rarity: 'epic', description: 'Shimmering gold for true champions' },
  { id: 'emerald', name: 'Emerald', category: 'neutral', price: 2.49, rarity: 'epic', description: 'Precious emerald green radiance' },
  { id: 'vintageMedieval', name: 'Vintage Medieval', category: 'neutral', price: 2.99, rarity: 'epic', description: 'Ancient carved wooden style' },
  { id: 'bwMinimalist', name: 'B&W Minimalist', category: 'neutral', price: 1.99, rarity: 'rare', description: 'Clean black and white design' },
  { id: 'bwDark', name: 'B&W Dark', category: 'neutral', price: 1.99, rarity: 'rare', description: 'Dark minimalist style' },
  { id: 'gold3D', name: 'Gold 3D Premium', category: 'neutral', price: 4.99, rarity: 'legendary', description: '3D rendered gold shield - ultimate prestige' },
  { id: 'enhancedClarity', name: 'Enhanced Clarity', category: 'neutral', price: 2.99, rarity: 'epic', description: 'Crystal clear high-definition' },
  // === GEMSTONE DUKES ===
  { id: 'amethystPurple', name: 'Amethyst Purple', category: 'neutral', price: 3.49, rarity: 'legendary', description: 'Beautiful stained-glass purple gemstone' },
  { id: 'sapphireBlue', name: 'Sapphire Blue', category: 'neutral', price: 3.49, rarity: 'legendary', description: 'Beautiful stained-glass blue gemstone' },
  // === KINGDOM DUKES ===
  { id: 'stoneSand', name: 'Stone & Sand', category: 'neutral', price: 3.49, rarity: 'legendary', description: 'Epic sand golem warrior shield' },
  { id: 'woodlinWarriors', name: 'Woodlin Warriors', category: 'neutral', price: 3.49, rarity: 'legendary', description: 'Ancient forest tree warrior shield' },
  // === COLOR CUSTOMIZABLE DUKE PIECES ===
  { id: 'spartanShield', name: 'Spartan Shield', category: 'neutral', price: 2.99, rarity: 'rare', description: 'Legendary Spartan helmet shield - ADAPTS TO YOUR PIECE THEME COLOR!', colorizable: true },
  { id: 'crownShield', name: 'Crown Shield', category: 'neutral', price: 2.99, rarity: 'rare', description: 'Royal crown shield emblem - ADAPTS TO YOUR PIECE THEME COLOR!', colorizable: true },
  // === FLAME SHIELD DUKES (sold as sets only) ===
  { id: 'flameWarrior', name: 'Flame Warrior', category: 'neutral', price: 3.99, rarity: 'legendary', description: 'Fight to be King duke - Warrior shield wrapped in flames', colorizable: true, setOnly: true, setId: 'flameSet' },
  { id: 'flameCrown', name: 'Flame Crown', category: 'neutral', price: 3.99, rarity: 'legendary', description: 'Instant King duke - Royal crown engulfed in eternal flames', colorizable: true, setOnly: true, setId: 'flameSet' },
  // === FLAME SHIELD V2 (sold as sets only) ===
  { id: 'flameWarriorV2', name: 'Flame Warrior V2', category: 'neutral', price: 4.99, rarity: 'legendary', description: 'Fight to be King duke - Premium line-art warrior shield', colorizable: true, setOnly: true, setId: 'flameSetV2' },
  { id: 'flameCrownV2', name: 'Flame Crown V2', category: 'neutral', price: 4.99, rarity: 'legendary', description: 'Instant King duke - Premium line-art crown shield', colorizable: true, setOnly: true, setId: 'flameSetV2' }
];

// Duke Skin Sets - variant-paired skins that must be purchased together
const DUKE_SKIN_SETS = [
  {
    id: 'flameSet',
    name: 'Flame Shield Set',
    price: 6.99,
    originalPrice: 7.98,
    rarity: 'legendary',
    description: 'Fight to be King + Instant King flame duke pair. ADAPTS TO YOUR PIECE THEME COLOR!',
    skins: ['flameWarrior', 'flameCrown'],
    labels: { flameWarrior: 'Fight to be King', flameCrown: 'Instant King' },
  },
  {
    id: 'flameSetV2',
    name: 'Flame Shield V2 Set',
    price: 8.99,
    originalPrice: 9.98,
    rarity: 'legendary',
    description: 'Premium line-art flame duke pair. ADAPTS TO YOUR PIECE THEME COLOR!',
    skins: ['flameWarriorV2', 'flameCrownV2'],
    labels: { flameWarriorV2: 'Fight to be King', flameCrownV2: 'Instant King' },
  },
];

// Board & Piece Customization unlock
const BOARD_CUSTOMIZATION_PRICE = 1.99;

// Chess Piece Skins catalog - comprehensive list
const PIECE_SKIN_CATALOG = [
  // === FREE OPEN SOURCE COLLECTION (GPLv2+, CC BY-SA 4.0, CC BY 4.0) ===
  {
    id: 'neo',
    name: 'Neo White',
    price: 0,
    rarity: 'common',
    description: 'Clean modern chess pieces by Colin M.L. Burnett (GPLv2+)',
    preview: [
      'https://raw.githubusercontent.com/lichess-org/lila/master/public/piece/cburnett/wK.svg',
      'https://raw.githubusercontent.com/lichess-org/lila/master/public/piece/cburnett/wQ.svg',
      'https://raw.githubusercontent.com/lichess-org/lila/master/public/piece/cburnett/wN.svg'
    ],
    category: 'free',
    isFree: true
  },
  {
    id: 'neoDark',
    name: 'Neo Black',
    price: 0,
    rarity: 'common',
    description: 'Clean modern black pieces by Colin M.L. Burnett (GPLv2+)',
    preview: [
      'https://raw.githubusercontent.com/lichess-org/lila/master/public/piece/cburnett/bK.svg',
      'https://raw.githubusercontent.com/lichess-org/lila/master/public/piece/cburnett/bQ.svg',
      'https://raw.githubusercontent.com/lichess-org/lila/master/public/piece/cburnett/bN.svg'
    ],
    category: 'free',
    isFree: true
  },
  {
    id: 'merida',
    name: 'Merida White',
    price: 0,
    rarity: 'common',
    description: 'Classic tournament style by Armando H. Marroquin (GPLv2+)',
    preview: [
      'https://raw.githubusercontent.com/lichess-org/lila/master/public/piece/merida/wK.svg',
      'https://raw.githubusercontent.com/lichess-org/lila/master/public/piece/merida/wQ.svg',
      'https://raw.githubusercontent.com/lichess-org/lila/master/public/piece/merida/wN.svg'
    ],
    category: 'free',
    isFree: true
  },
  {
    id: 'meridaDark',
    name: 'Merida Black',
    price: 0,
    rarity: 'common',
    description: 'Classic tournament black pieces by Armando H. Marroquin (GPLv2+)',
    preview: [
      'https://raw.githubusercontent.com/lichess-org/lila/master/public/piece/merida/bK.svg',
      'https://raw.githubusercontent.com/lichess-org/lila/master/public/piece/merida/bQ.svg',
      'https://raw.githubusercontent.com/lichess-org/lila/master/public/piece/merida/bN.svg'
    ],
    category: 'free',
    isFree: true
  },
  {
    id: 'shapes',
    name: 'Shapes White',
    price: 0,
    rarity: 'common',
    description: 'Abstract geometric pieces by flugsio (CC BY-SA 4.0)',
    preview: [
      'https://raw.githubusercontent.com/lichess-org/lila/master/public/piece/shapes/wK.svg',
      'https://raw.githubusercontent.com/lichess-org/lila/master/public/piece/shapes/wQ.svg',
      'https://raw.githubusercontent.com/lichess-org/lila/master/public/piece/shapes/wN.svg'
    ],
    category: 'free',
    isFree: true
  },
  {
    id: 'shapesDark',
    name: 'Shapes Black',
    price: 0,
    rarity: 'common',
    description: 'Abstract geometric black pieces by flugsio (CC BY-SA 4.0)',
    preview: [
      'https://raw.githubusercontent.com/lichess-org/lila/master/public/piece/shapes/bK.svg',
      'https://raw.githubusercontent.com/lichess-org/lila/master/public/piece/shapes/bQ.svg',
      'https://raw.githubusercontent.com/lichess-org/lila/master/public/piece/shapes/bN.svg'
    ],
    category: 'free',
    isFree: true
  },
  {
    id: 'kiwenSuwi',
    name: 'Kiwen Suwi White',
    price: 0,
    rarity: 'common',
    description: 'Stylized minimalist pieces by neverRare (CC BY 4.0)',
    preview: [
      'https://raw.githubusercontent.com/lichess-org/lila/master/public/piece/kiwen-suwi/wK.svg',
      'https://raw.githubusercontent.com/lichess-org/lila/master/public/piece/kiwen-suwi/wQ.svg',
      'https://raw.githubusercontent.com/lichess-org/lila/master/public/piece/kiwen-suwi/wN.svg'
    ],
    category: 'free',
    isFree: true
  },
  {
    id: 'kiwenSuwiDark',
    name: 'Kiwen Suwi Black',
    price: 0,
    rarity: 'common',
    description: 'Stylized minimalist black pieces by neverRare (CC BY 4.0)',
    preview: [
      'https://raw.githubusercontent.com/lichess-org/lila/master/public/piece/kiwen-suwi/bK.svg',
      'https://raw.githubusercontent.com/lichess-org/lila/master/public/piece/kiwen-suwi/bQ.svg',
      'https://raw.githubusercontent.com/lichess-org/lila/master/public/piece/kiwen-suwi/bN.svg'
    ],
    category: 'free',
    isFree: true
  },
  // === 3D PREMIUM SETS ===
  {
    id: 'golden',
    name: 'Golden 3D',
    price: 4.99,
    rarity: 'legendary',
    description: 'Luxurious gold metallic chess pieces with premium 3D rendering',
    preview: [
      'https://customer-assets.emergentagent.com/job_85ebdb0b-c856-4baf-9e55-ba0cd8064999/artifacts/zmrne0bi_King.png',
      'https://customer-assets.emergentagent.com/job_boardbattles-3/artifacts/p2wnhr52_Queen.png',
      'https://customer-assets.emergentagent.com/job_boardbattles-3/artifacts/60qlkbm7_Knight.png'
    ],
    category: 'premium'
  },
  {
    id: 'obsidian',
    name: 'Obsidian 3D',
    price: 4.99,
    rarity: 'legendary',
    description: 'Sleek glossy black pieces with premium 3D finish',
    preview: [
      'https://customer-assets.emergentagent.com/job_85ebdb0b-c856-4baf-9e55-ba0cd8064999/artifacts/d0p2uu4l_black%20kingpng.png',
      'https://customer-assets.emergentagent.com/job_boardbattles-3/artifacts/rrxl5ktg_blackqueen%29.png',
      'https://customer-assets.emergentagent.com/job_boardbattles-3/artifacts/gqsl7hca_horseknight.png'
    ],
    category: 'premium'
  },
  // === VINTAGE SETS ===
  {
    id: 'vintage',
    name: 'Vintage White',
    price: 3.99,
    rarity: 'epic',
    description: 'Hand-drawn engraved medieval white pieces',
    preview: [
      'https://customer-assets.emergentagent.com/job_boardbattles-3/artifacts/2tx2ybcg_King.png',
      'https://customer-assets.emergentagent.com/job_boardbattles-3/artifacts/hk7etiwm_Queen.png',
      'https://customer-assets.emergentagent.com/job_boardbattles-3/artifacts/hftt23bi_Knight.png'
    ],
    category: 'vintage'
  },
  {
    id: 'vintageBlack',
    name: 'Vintage Black',
    price: 3.99,
    rarity: 'epic',
    description: 'Dark hand-drawn engraved medieval style - pairs with Vintage White',
    preview: [
      'https://customer-assets.emergentagent.com/job_boardware-custom/artifacts/ttl3facz_blkking.png',
      'https://customer-assets.emergentagent.com/job_boardware-custom/artifacts/b5g7rvii_blkqueen.png',
      'https://customer-assets.emergentagent.com/job_boardware-custom/artifacts/69xjqj40_blkknight.png'
    ],
    category: 'vintage'
  },
  {
    id: 'vintageMedieval',
    name: 'Vintage Medieval',
    price: 3.99,
    rarity: 'epic',
    description: 'Carved wooden/bronze style pieces with warm tones',
    preview: [
      'https://customer-assets.emergentagent.com/job_85ebdb0b-c856-4baf-9e55-ba0cd8064999/artifacts/5y425oo1_King%20.png',
      'https://customer-assets.emergentagent.com/job_85ebdb0b-c856-4baf-9e55-ba0cd8064999/artifacts/5q4dulgc_Queen.png',
      'https://customer-assets.emergentagent.com/job_85ebdb0b-c856-4baf-9e55-ba0cd8064999/artifacts/iqk0mqo1_knight.png'
    ],
    category: 'vintage'
  },
  // === KAWAII SETS ===
  {
    id: 'kawaii',
    name: 'Kawaii Brown',
    price: 2.99,
    rarity: 'rare',
    description: 'Cute brown emoji pieces with happy faces',
    preview: [
      'https://customer-assets.emergentagent.com/job_boardbattles-3/artifacts/9u70ags6_Brown%20King.png',
      'https://customer-assets.emergentagent.com/job_boardware-custom/artifacts/3k5p9kj0_Brown%20Queen.png',
      'https://customer-assets.emergentagent.com/job_boardware-custom/artifacts/xuh2s6z3_Brown%20knight.png'
    ],
    category: 'kawaii'
  },
  {
    id: 'kawaiiWhite',
    name: 'Kawaii White',
    price: 2.99,
    rarity: 'rare',
    description: 'Cute white emoji pieces with happy faces',
    preview: [
      'https://customer-assets.emergentagent.com/job_boardbattles-3/artifacts/r1bm2e11_White%20King%20.png',
      'https://customer-assets.emergentagent.com/job_boardware-custom/artifacts/njnquebi_White%20Queen.png',
      'https://customer-assets.emergentagent.com/job_boardware-custom/artifacts/cd0oosfn_White%20Knight.png'
    ],
    category: 'kawaii'
  },
  {
    id: 'kawaiiBlack',
    name: 'Kawaii Black',
    price: 2.99,
    rarity: 'rare',
    description: 'Cute black emoji pieces with gold accents',
    preview: [
      'https://customer-assets.emergentagent.com/job_boardbattles-3/artifacts/omu9ftg2_Black%20King.png',
      'https://customer-assets.emergentagent.com/job_boardware-custom/artifacts/79hg6q9r_Black%20Queen.png',
      'https://customer-assets.emergentagent.com/job_boardware-custom/artifacts/6k32omak_Blackknight.png'
    ],
    category: 'kawaii'
  },
  {
    id: 'kawaiiBeige',
    name: 'Kawaii Beige',
    price: 2.99,
    rarity: 'rare',
    description: 'Cute beige/tan emoji pieces - pairs with Kawaii Brown',
    preview: [
      'https://customer-assets.emergentagent.com/job_boardware-custom/artifacts/0k72km65_baige%20queen.png',
      'https://customer-assets.emergentagent.com/job_boardware-custom/artifacts/0zbap5z8_baige%20knight.png',
      'https://customer-assets.emergentagent.com/job_boardware-custom/artifacts/5uwi6k96_baige%20pawn.png'
    ],
    category: 'kawaii'
  },
  // === FABLE MEDIEVAL SETS ===
  {
    id: 'fableWhite',
    name: 'Fable White',
    price: 3.99,
    rarity: 'epic',
    description: 'Medieval crusader knights in white/silver armor',
    preview: [
      'https://customer-assets.emergentagent.com/job_85ebdb0b-c856-4baf-9e55-ba0cd8064999/artifacts/o29b9eux_Fable%20King%20white.png',
      'https://customer-assets.emergentagent.com/job_85ebdb0b-c856-4baf-9e55-ba0cd8064999/artifacts/g9m3aoue_Fable%20Queen%20white.png',
      'https://customer-assets.emergentagent.com/job_85ebdb0b-c856-4baf-9e55-ba0cd8064999/artifacts/lku3lwyb_Fable%20Knight%20white.png'
    ],
    category: 'fable'
  },
  {
    id: 'fableBlack',
    name: 'Fable Black',
    price: 3.99,
    rarity: 'epic',
    description: 'Medieval armored warriors in dark style',
    preview: [
      'https://customer-assets.emergentagent.com/job_85ebdb0b-c856-4baf-9e55-ba0cd8064999/artifacts/ywqogvww_Fable%20King%20Black.png',
      'https://customer-assets.emergentagent.com/job_85ebdb0b-c856-4baf-9e55-ba0cd8064999/artifacts/6hi4uzne_Fable%20Queen%20Black.png',
      'https://customer-assets.emergentagent.com/job_85ebdb0b-c856-4baf-9e55-ba0cd8064999/artifacts/s3eiyag2_Fable%20Knight%20Black.png'
    ],
    category: 'fable'
  },
  // === GOTHIC SETS ===
  {
    id: 'gothicWhite',
    name: 'Gothic White',
    price: 3.99,
    rarity: 'epic',
    description: 'Radiant white gothic pieces with intricate details',
    preview: [
      'https://customer-assets.emergentagent.com/job_85ebdb0b-c856-4baf-9e55-ba0cd8064999/artifacts/k3afls8k_White%20KIng.png',
      'https://customer-assets.emergentagent.com/job_85ebdb0b-c856-4baf-9e55-ba0cd8064999/artifacts/sdijgv24_white%20Queen.png',
      'https://customer-assets.emergentagent.com/job_85ebdb0b-c856-4baf-9e55-ba0cd8064999/artifacts/ajdhm9p8_white%20Knight.png'
    ],
    category: 'gothic'
  },
  {
    id: 'gothicBlack',
    name: 'Gothic Black',
    price: 3.99,
    rarity: 'epic',
    description: 'Majestic dark gothic pieces with shadow effects',
    preview: [
      'https://customer-assets.emergentagent.com/job_85ebdb0b-c856-4baf-9e55-ba0cd8064999/artifacts/0hhcvpg7_Black%20KIng.png',
      'https://customer-assets.emergentagent.com/job_85ebdb0b-c856-4baf-9e55-ba0cd8064999/artifacts/mzb2cvnn_Black%20Queen.png',
      'https://customer-assets.emergentagent.com/job_85ebdb0b-c856-4baf-9e55-ba0cd8064999/artifacts/96uvm8uy_Black%20Knight.png'
    ],
    category: 'gothic'
  },
  // === COLOSSAL EPIC SETS ===
  {
    id: 'colossalWhite',
    name: 'Colossal White',
    price: 4.99,
    rarity: 'legendary',
    description: 'Epic marble/white stone sculpted titan pieces',
    preview: [
      'https://customer-assets.emergentagent.com/job_85ebdb0b-c856-4baf-9e55-ba0cd8064999/artifacts/mbxdsi5j_superKing%20white.png',
      'https://customer-assets.emergentagent.com/job_85ebdb0b-c856-4baf-9e55-ba0cd8064999/artifacts/6r8y2vvi_superQueen%20white.png',
      'https://customer-assets.emergentagent.com/job_85ebdb0b-c856-4baf-9e55-ba0cd8064999/artifacts/ohwrh7xe_superKnight%20white.png'
    ],
    category: 'colossal'
  },
  {
    id: 'colossalBronze',
    name: 'Colossal Bronze',
    price: 4.99,
    rarity: 'legendary',
    description: 'Epic dark bronze sculpted titan pieces',
    preview: [
      'https://customer-assets.emergentagent.com/job_85ebdb0b-c856-4baf-9e55-ba0cd8064999/artifacts/cm5sb8u6_superKing%20bronze.png',
      'https://customer-assets.emergentagent.com/job_85ebdb0b-c856-4baf-9e55-ba0cd8064999/artifacts/1f4hgkz7_superQueen%20bronze.png',
      'https://customer-assets.emergentagent.com/job_85ebdb0b-c856-4baf-9e55-ba0cd8064999/artifacts/my0phb7k_superKnight%20Bronze.png'
    ],
    category: 'colossal'
  },
  // === GEMSTONE SETS ===
  {
    id: 'amethystPurple',
    name: 'Amethyst Purple',
    price: 3.99,
    rarity: 'legendary',
    description: 'Beautiful stained-glass purple gemstone pieces',
    preview: [
      'https://customer-assets.emergentagent.com/job_chess-customizer/artifacts/uulzopfh_kingpurple.png',
      'https://customer-assets.emergentagent.com/job_chess-customizer/artifacts/pxr4cv34_Queenpurple.png',
      'https://customer-assets.emergentagent.com/job_chess-customizer/artifacts/jlo0m1hi_Knightpurple.png'
    ],
    category: 'gemstone'
  },
  {
    id: 'sapphireBlue',
    name: 'Sapphire Blue',
    price: 3.99,
    rarity: 'legendary',
    description: 'Beautiful stained-glass blue gemstone pieces',
    preview: [
      'https://customer-assets.emergentagent.com/job_chess-customizer/artifacts/dmet5a4g_kingpsapphireblue.png',
      'https://customer-assets.emergentagent.com/job_chess-customizer/artifacts/m0m8sku3_queensapphireblue.png',
      'https://customer-assets.emergentagent.com/job_chess-customizer/artifacts/sf1jmtkx_Knightsapphireblue.png'
    ],
    category: 'gemstone'
  },
  // === KINGDOM BATTLE SETS ===
  {
    id: 'stoneSand',
    name: 'Stone & Sand Kingdom',
    price: 4.49,
    rarity: 'legendary',
    description: 'Epic sand/stone golem warriors from the desert realm',
    preview: [
      'https://customer-assets.emergentagent.com/job_chess-customizer/artifacts/x9dczv8c_sandking2.png',
      'https://customer-assets.emergentagent.com/job_chess-customizer/artifacts/5hq29tpm_sandQueen.png',
      'https://customer-assets.emergentagent.com/job_chess-customizer/artifacts/sdjyiifk_sandKnight.png'
    ],
    category: 'kingdom'
  },
  {
    id: 'woodlinWarriors',
    name: 'Woodlin Warriors Kingdom',
    price: 4.49,
    rarity: 'legendary',
    description: 'Ancient forest tree warriors from the woodland realm',
    preview: [
      'https://customer-assets.emergentagent.com/job_chess-customizer/artifacts/u3nnk32c_woodlenrealking2.png',
      'https://customer-assets.emergentagent.com/job_chess-customizer/artifacts/iauu4nce_woodlenrealQueen2.png',
      'https://customer-assets.emergentagent.com/job_chess-customizer/artifacts/e0b84qjk_woodlenKnight.png'
    ],
    category: 'kingdom'
  }
];

// Themed Chess Sets - Complete theme packs
const THEMED_SETS = [
  {
    id: 'royal-gold',
    name: 'Royal Gold',
    description: 'Golden pieces + Gold board theme + Gold duke skins',
    price: 9.99,
    rarity: 'legendary',
    includes: { pieces: 'golden', dukeWhite: 'gold', dukeBlack: 'gold', boardTheme: 'royal-gold' }
  },
  {
    id: 'dark-knight',
    name: 'Dark Knight',
    description: 'Obsidian pieces + Dark board theme + Dark duke skins',
    price: 9.99,
    rarity: 'legendary',
    includes: { pieces: 'obsidian', dukeWhite: 'dark', dukeBlack: 'darkSteel', boardTheme: 'dark-knight' }
  },
  {
    id: 'vintage-classic',
    name: 'Vintage Classic',
    description: 'Vintage pieces + Wood board theme + Medieval duke skins',
    price: 8.99,
    rarity: 'epic',
    includes: { pieces: 'vintage', dukeWhite: 'vintageMedieval', dukeBlack: 'vintageMedieval', boardTheme: 'wood' }
  },
  {
    id: 'gemstone-battle',
    name: 'Gemstone Battle',
    description: 'Amethyst vs Sapphire - Epic gemstone warfare',
    price: 11.99,
    rarity: 'legendary',
    includes: { piecesWhite: 'amethystPurple', piecesBlack: 'sapphireBlue', dukeWhite: 'amethystPurple', dukeBlack: 'sapphireBlue' }
  },
  {
    id: 'kingdom-clash',
    name: 'Kingdom Clash',
    description: 'Stone & Sand vs Woodlin Warriors - Ultimate faction war',
    price: 12.99,
    rarity: 'legendary',
    includes: { piecesWhite: 'stoneSand', piecesBlack: 'woodlinWarriors', dukeWhite: 'stoneSand', dukeBlack: 'woodlinWarriors' }
  },
  {
    id: 'kawaii-bundle',
    name: 'Kawaii Collection',
    description: 'All 4 Kawaii sets - Cute pieces for every mood!',
    price: 8.99,
    rarity: 'epic',
    includes: { pieces: ['kawaii', 'kawaiiWhite', 'kawaiiBlack', 'kawaiiBeige'] }
  },
  {
    id: 'colossal-titans',
    name: 'Colossal Titans',
    description: 'White Marble vs Bronze Giants - Titanic battle',
    price: 12.99,
    rarity: 'legendary',
    includes: { piecesWhite: 'colossalWhite', piecesBlack: 'colossalBronze', dukeWhite: 'colossalWhite', dukeBlack: 'colossalBronze' }
  },
  {
    id: 'gothic-shadows',
    name: 'Gothic Shadows',
    description: 'Gothic White vs Gothic Black - Dark elegance',
    price: 10.99,
    rarity: 'legendary',
    includes: { piecesWhite: 'gothicWhite', piecesBlack: 'gothicBlack' }
  }
];

// Calculate bundle prices
const PAID_DUKE_SKINS = DUKE_SKIN_CATALOG.filter(s => !s.isFree);
const DUKE_BUNDLE_PRICE = Math.round(PAID_DUKE_SKINS.reduce((sum, s) => sum + s.price, 0) * 0.6 * 100) / 100;
const DUKE_ORIGINAL_PRICE = PAID_DUKE_SKINS.reduce((sum, s) => sum + s.price, 0);
const PIECE_BUNDLE_PRICE = Math.round(PIECE_SKIN_CATALOG.reduce((sum, s) => sum + s.price, 0) * 0.6 * 100) / 100;
const PIECE_ORIGINAL_PRICE = PIECE_SKIN_CATALOG.reduce((sum, s) => sum + s.price, 0);

const SkinStore = ({ isOpen, onClose, onSkinSelect }) => {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('themed'); // 'themed', 'pieces', 'duke'
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [ownedDukeSkins, setOwnedDukeSkins] = useState(['default']);
  const [ownedPieceSkins, setOwnedPieceSkins] = useState([]);
  const [ownedThemedSets, setOwnedThemedSets] = useState([]);
  const [processingPurchase, setProcessingPurchase] = useState(null);
  const [previewSkin, setPreviewSkin] = useState(null);
  const [equippedPieceSkin, setEquippedPieceSkin] = useState('default');
  const [equippedDukeSkinWhite, setEquippedDukeSkinWhite] = useState('default');
  const [equippedDukeSkinBlack, setEquippedDukeSkinBlack] = useState('default');
  const [equippedThemedSet, setEquippedThemedSet] = useState(null);
  const [isPurchaseLocked, setIsPurchaseLocked] = useState(true); // All purchases locked
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [boardCustomizationUnlocked, setBoardCustomizationUnlocked] = useState(false);
  const [boardCustProcessing, setBoardCustProcessing] = useState(false);
  const [giftModalSet, setGiftModalSet] = useState(null);
  const [giftRecipient, setGiftRecipient] = useState('');
  const [giftMessage, setGiftMessage] = useState('');
  const [giftProcessing, setGiftProcessing] = useState(false);

  useEffect(() => {
    if (isOpen && token) {
      fetchOwnedSkins();
      fetchSubscriptionStatus();
    }
    // Load currently equipped skins from localStorage
    const settings = JSON.parse(localStorage.getItem('chessKingdomSettings') || '{}');
    setEquippedPieceSkin(settings.pieceSkin || 'default');
    setEquippedDukeSkinWhite(settings.dukeSkinWhite || 'default');
    setEquippedDukeSkinBlack(settings.dukeSkinBlack || 'default');
    setEquippedThemedSet(settings.themedSet || null);
    
    // Check for subscription success redirect
    if (isOpen) {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('subscription') === 'success') {
        window.history.replaceState({}, '', window.location.pathname);
        // Re-fetch after successful subscription
        if (token) {
          setTimeout(() => {
            fetchOwnedSkins();
            fetchSubscriptionStatus();
          }, 2000);
        }
      }
    }
  }, [isOpen, token]);

  const fetchOwnedSkins = async () => {
    try {
      const response = await fetch(`${API}/skins/owned`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOwnedDukeSkins(['default', ...(data.owned_duke_skins || [])]);
        setOwnedPieceSkins(data.owned_piece_skins || []);
        setOwnedThemedSets(data.owned_themed_sets || []);
        setBoardCustomizationUnlocked(!!data.board_customization_unlocked);
      }
    } catch (error) {
      logger.error('Error fetching owned skins:', error);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await fetch(`${API}/subscription/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setIsSubscribed(data.subscribed);
      }
    } catch (error) {
      logger.error('Error fetching subscription status:', error);
    }
  };

  const handleSubscribe = async () => {
    if (!token) {
      alert('Please login to subscribe');
      return;
    }
    setSubscriptionLoading(true);
    try {
      const response = await fetch(`${API}/subscription/checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ origin_url: window.location.origin })
      });
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        const err = await response.json();
        alert(err.detail || 'Failed to start subscription checkout');
      }
    } catch (error) {
      alert('Error connecting to payment service');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handlePurchase = async (skinId, skinType) => {
    if (!token) {
      alert('Please login to purchase skins');
      return;
    }
    if (isPurchaseLocked) {
      alert('Store purchases are currently locked. Only authorized users can purchase.');
      return;
    }
    setProcessingPurchase(skinId);
    try {
      const response = await fetch(`${API}/skins/purchase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          skin_id: skinId,
          skin_type: skinType,
          origin_url: window.location.origin
        })
      });
      const data = await response.json();
      if (response.ok && data.url) {
        window.location.href = data.url;
      } else if (response.ok && data.success) {
        alert('Skin unlocked!');
        fetchOwnedSkins();
      } else {
        alert(data.detail || 'Failed to process purchase');
      }
    } catch (error) {
      logger.error('Purchase error:', error);
      alert('Failed to process purchase. Please try again.');
    } finally {
      setProcessingPurchase(null);
    }
  };

  const handlePurchaseSet = async (setId) => {
    if (!token) { alert('Please login to purchase'); return; }
    setProcessingPurchase(setId);
    try {
      const response = await fetch(`${API}/duke-skins/purchase-set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ set_id: setId, origin_url: window.location.origin })
      });
      const data = await response.json();
      if (response.ok && data.url) {
        window.location.href = data.url;
      } else {
        alert(data.detail || 'Failed to process purchase');
      }
    } catch (error) {
      alert('Failed to process purchase. Please try again.');
    } finally {
      setProcessingPurchase(null);
    }
  };

  const handlePurchaseBoardCustomization = async () => {
    if (!token) { alert('Please login to purchase'); return; }
    setBoardCustProcessing(true);
    try {
      const response = await fetch(`${API}/board-customization/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ origin_url: window.location.origin })
      });
      const data = await response.json();
      if (response.ok && data.url) {
        window.location.href = data.url;
      } else if (response.ok && data.success) {
        setBoardCustomizationUnlocked(true);
        alert('Board customization already unlocked!');
      } else {
        alert(data.detail || 'Failed to process purchase');
      }
    } catch (error) {
      alert('Failed to process purchase. Please try again.');
    } finally {
      setBoardCustProcessing(false);
    }
  };

  const handleGiftSet = async () => {
    if (!token || !giftModalSet) return;
    if (!giftRecipient.trim()) { alert('Please enter a username or email'); return; }
    setGiftProcessing(true);
    try {
      const response = await fetch(`${API}/duke-skins/gift-set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ set_id: giftModalSet.id, recipient_identifier: giftRecipient.trim(), message: giftMessage.trim() })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        alert(data.message);
        setGiftModalSet(null);
        setGiftRecipient('');
        setGiftMessage('');
      } else {
        alert(data.detail || 'Failed to send gift');
      }
    } catch (error) {
      alert('Failed to send gift. Please try again.');
    } finally {
      setGiftProcessing(false);
    }
  };



  const handleEquipDukeSkin = (skinId) => {
    if (ownedDukeSkins.includes(skinId)) {
      const skin = DUKE_SKIN_CATALOG.find(s => s.id === skinId);
      const settings = JSON.parse(localStorage.getItem('chessKingdomSettings') || '{}');
      if (skin?.category === 'white') {
        settings.dukeSkinWhite = skinId;
        setEquippedDukeSkinWhite(skinId);
      } else if (skin?.category === 'black') {
        settings.dukeSkinBlack = skinId;
        setEquippedDukeSkinBlack(skinId);
      } else {
        settings.dukeSkinWhite = skinId;
        settings.dukeSkinBlack = skinId;
        setEquippedDukeSkinWhite(skinId);
        setEquippedDukeSkinBlack(skinId);
      }
      localStorage.setItem('chessKingdomSettings', JSON.stringify(settings));
      if (onSkinSelect) onSkinSelect('duke', skinId);
      window.dispatchEvent(new Event('settingsChanged'));
      alert(`${skin?.name} Duke skin equipped!`);
    }
  };

  const handleEquipPieceSkin = (skinId) => {
    if (ownedPieceSkins.includes(skinId)) {
      const settings = JSON.parse(localStorage.getItem('chessKingdomSettings') || '{}');
      settings.pieceSkin = skinId;
      localStorage.setItem('chessKingdomSettings', JSON.stringify(settings));
      setEquippedPieceSkin(skinId);
      if (onSkinSelect) onSkinSelect('piece', skinId);
      window.dispatchEvent(new Event('settingsChanged'));
      alert(`${PIECE_SKIN_CATALOG.find(s => s.id === skinId)?.name} piece skin equipped!`);
    }
  };

  const handleEquipThemedSet = (setId) => {
    const set = THEMED_SETS.find(s => s.id === setId);
    if (!set) return;
    
    // Check if owned or just selecting
    const isOwned = ownedThemedSets.includes(setId);
    
    const settings = JSON.parse(localStorage.getItem('chessKingdomSettings') || '{}');
    settings.themedSet = setId;
    localStorage.setItem('chessKingdomSettings', JSON.stringify(settings));
    setEquippedThemedSet(setId);
    window.dispatchEvent(new Event('settingsChanged'));
    
    if (isOwned) {
      alert(`${set.name} theme equipped!`);
    } else {
      alert(`${set.name} theme selected! Purchase to unlock.`);
    }
  };

  const filteredDukeSkins = (selectedCategory === 'all' 
    ? DUKE_SKIN_CATALOG 
    : DUKE_SKIN_CATALOG.filter(s => s.category === selectedCategory)
  ).filter(s => !s.setOnly);

  const pieceCategories = [
    { id: 'all', labelKey: 'store.categories.all', icon: '🎨' },
    { id: 'free', labelKey: 'store.categories.free', icon: '🆓' },
    { id: 'premium', labelKey: 'store.categories.premium', icon: '✨' },
    { id: 'vintage', labelKey: 'store.categories.vintage', icon: '📜' },
    { id: 'kawaii', labelKey: 'store.categories.kawaii', icon: '😊' },
    { id: 'fable', labelKey: 'store.categories.fable', icon: '⚔️' },
    { id: 'gothic', labelKey: 'store.categories.gothic', icon: '🏰' },
    { id: 'colossal', labelKey: 'store.categories.colossal', icon: '🏛️' },
    { id: 'gemstone', labelKey: 'store.categories.gemstone', icon: '💎' },
    { id: 'kingdom', labelKey: 'store.categories.kingdom', icon: '👑' }
  ];

  const dukeCategories = [
    { id: 'all', labelKey: 'store.categories.all', icon: '🎨' },
    { id: 'white', labelKey: 'store.categories.white', icon: '⬜' },
    { id: 'black', labelKey: 'store.categories.black', icon: '⬛' },
    { id: 'neutral', labelKey: 'store.categories.neutral', icon: '🔶' }
  ];

  const filteredPieceSkins = selectedCategory === 'all'
    ? PIECE_SKIN_CATALOG
    : PIECE_SKIN_CATALOG.filter(s => s.category === selectedCategory);

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'common': return '#9ca3af';
      case 'rare': return '#3b82f6';
      case 'epic': return '#8b5cf6';
      case 'legendary': return '#f59e0b';
      default: return '#9ca3af';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="duke-store-overlay" onClick={onClose} data-testid="skin-store-overlay">
      <div className="duke-store-modal skin-store-modal" onClick={(e) => e.stopPropagation()}>
        <button className="duke-store-close" onClick={onClose} data-testid="skin-store-close">×</button>
        
        <div className="duke-store-header">
          <h2>🎨 {t('store.title', 'Skin Store')}</h2>
          <p className="duke-store-subtitle">{t('store.subtitle', 'Premium chess piece skins, duke shields & themed sets')}</p>
        </div>

        {/* Subscription Banner - only show if not subscribed AND not full-access user AND not on trial */}
        {!isSubscribed && token && ownedThemedSets.length < 8 && (
          <div className="subscription-banner" data-testid="subscription-banner">
            <div className="subscription-content">
              <div className="subscription-info">
                <h3>Unlock Everything</h3>
                <p>Get instant access to <strong>all 80+ skins, duke shields & themed sets</strong> plus future content</p>
              </div>
              <div className="subscription-cta">
                <div className="subscription-price">
                  <span className="price-amount">$4.99</span>
                  <span className="price-period">/month</span>
                </div>
                <button
                  className="subscribe-btn"
                  onClick={handleSubscribe}
                  disabled={subscriptionLoading}
                  data-testid="subscribe-btn"
                >
                  {subscriptionLoading ? 'Loading...' : 'Subscribe Now'}
                </button>
              </div>
            </div>
          </div>
        )}
        {(isSubscribed || ownedThemedSets.length >= 8) && (
          <div className="subscription-active-badge" data-testid="subscription-active">
            <span>{ownedThemedSets.length >= 8 && !isSubscribed ? 'Full Access Member' : 'Full Access Subscriber'}</span>
          </div>
        )}

        {/* Main Tabs */}
        <div className="store-tabs">
          <button 
            className={`store-tab ${activeTab === 'themed' ? 'active' : ''}`}
            onClick={() => { setActiveTab('themed'); setSelectedCategory('all'); }}
            data-testid="tab-themed"
          >
            👑 {t('store.themedSets', 'Themed Sets')}
          </button>
          <button 
            className={`store-tab ${activeTab === 'pieces' ? 'active' : ''}`}
            onClick={() => { setActiveTab('pieces'); setSelectedCategory('all'); }}
            data-testid="tab-pieces"
          >
            ♟️ {t('store.pieceSkins', 'Piece Skins')}
          </button>
          <button 
            className={`store-tab ${activeTab === 'duke' ? 'active' : ''}`}
            onClick={() => { setActiveTab('duke'); setSelectedCategory('all'); }}
            data-testid="tab-duke"
          >
            🛡️ {t('store.dukeSkins', 'Duke Skins')}
          </button>
        </div>

        {/* THEMED SETS TAB */}
        {activeTab === 'themed' && (
          <>
            <div className="themed-sets-intro">
              <h3>🏆 {t('store.completePacks', 'Complete Theme Packs')}</h3>
              <p>{t('store.completePacksDesc', 'Get matching pieces, dukes, and board themes at a discount!')}</p>
            </div>
            <div className="themed-sets-grid">
              {THEMED_SETS.map(set => {
                const isOwned = ownedThemedSets.includes(set.id);
                const isEquipped = equippedThemedSet === set.id;
                return (
                  <div 
                    key={set.id} 
                    className={`themed-set-card ${set.rarity} ${isEquipped ? 'equipped' : ''} ${isOwned ? 'owned' : ''}`}
                    onClick={() => handleEquipThemedSet(set.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    {isEquipped && <div className="equipped-badge themed-equipped">✨ SELECTED</div>}
                    <div className="themed-set-header">
                      <span className="rarity-tag" style={{ backgroundColor: getRarityColor(set.rarity) }}>
                        {set.rarity}
                      </span>
                      <h4>{set.name}</h4>
                    </div>
                    <p className="themed-set-description">{set.description}</p>
                    <div className="themed-set-price">
                      {isOwned ? '✓ Owned' : `$${set.price.toFixed(2)}`}
                    </div>
                    {!isOwned && (
                      <button 
                        className={`buy-btn themed-buy ${isPurchaseLocked ? 'locked' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePurchase(set.id, 'themed-set');
                        }}
                        disabled={processingPurchase === set.id || isPurchaseLocked}
                      >
                        {processingPurchase === set.id ? 'Processing...' : isPurchaseLocked ? '🔒 Locked' : 'Unlock Set'}
                      </button>
                    )}
                    {isOwned && !isEquipped && (
                      <button 
                        className="buy-btn equip-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEquipThemedSet(set.id);
                        }}
                      >
                        Equip Theme
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* PIECE SKINS TAB */}
        {activeTab === 'pieces' && (
          <>
            {/* Bundle Offer */}
            <div className="bundle-banner piece-bundle">
              <div className="bundle-content">
                <div className="bundle-icon">✨</div>
                <div className="bundle-info">
                  <h3>All Piece Skins Bundle</h3>
                  <p>40% OFF - Get all {PIECE_SKIN_CATALOG.length} piece skins!</p>
                  <div className="bundle-pricing">
                    <span className="original-price">${PIECE_ORIGINAL_PRICE.toFixed(2)}</span>
                    <span className="bundle-price">${PIECE_BUNDLE_PRICE.toFixed(2)}</span>
                  </div>
                </div>
                <button 
                  className={`bundle-buy-btn ${isPurchaseLocked ? 'locked' : ''}`}
                  onClick={() => handlePurchase('piece-bundle', 'piece-bundle')}
                  disabled={processingPurchase === 'piece-bundle' || isPurchaseLocked}
                >
                  {processingPurchase === 'piece-bundle' ? 'Processing...' : isPurchaseLocked ? '🔒 Locked' : 'Buy Bundle'}
                </button>
              </div>
            </div>

            {/* Category Filter */}
            <div className="duke-store-categories">
              {pieceCategories.map(cat => (
                <button
                  key={cat.id}
                  className={`category-btn ${selectedCategory === cat.id ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(cat.id)}
                >
                  <span className="cat-icon">{cat.icon}</span>
                  <span className="cat-label">{t(cat.labelKey)}</span>
                </button>
              ))}
            </div>

            {/* Piece Skins Grid */}
            <div className="duke-skins-grid piece-skins-grid">
              {filteredPieceSkins.map(skin => {
                const isOwned = ownedPieceSkins.includes(skin.id);
                const isEquipped = equippedPieceSkin === skin.id;
                const isPurchasing = processingPurchase === skin.id;
                return (
                  <div 
                    key={skin.id} 
                    className={`duke-skin-card piece-skin-card ${isOwned ? 'owned' : ''} ${isEquipped ? 'equipped' : ''}`}
                    onMouseEnter={() => setPreviewSkin(skin)}
                    onMouseLeave={() => setPreviewSkin(null)}
                  >
                    <div className="skin-preview piece-preview">
                      <div className="piece-skin-images">
                        {skin.preview.map((img) => (
                          <img key={img} src={img} alt={skin.name} className="piece-skin-img" />
                        ))}
                      </div>
                      <span className="rarity-tag" style={{ backgroundColor: getRarityColor(skin.rarity) }}>
                        {skin.rarity}
                      </span>
                      {isOwned && <div className="owned-badge">✓ Owned</div>}
                      {isEquipped && <div className="equipped-badge">⚔️ Equipped</div>}
                    </div>
                    <div className="skin-details">
                      <h4 className="skin-name">{skin.name}</h4>
                      <p className="skin-description">{skin.description}</p>
                      <div className="skin-actions">
                        {isOwned ? (
                          <button 
                            className={`equip-btn ${isEquipped ? 'equipped-btn' : ''}`} 
                            onClick={() => handleEquipPieceSkin(skin.id)}
                            disabled={isEquipped}
                          >
                            {isEquipped ? '✓ Equipped' : 'Equip'}
                          </button>
                        ) : (
                          <button 
                            className={`buy-btn ${isPurchaseLocked ? 'locked' : ''}`}
                            onClick={() => handlePurchase(skin.id, 'piece')}
                            disabled={isPurchasing || isPurchaseLocked}
                          >
                            {isPurchasing ? 'Processing...' : isPurchaseLocked ? '🔒 Locked' : `$${skin.price.toFixed(2)}`}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* DUKE SKINS TAB */}
        {activeTab === 'duke' && (
          <>
            {/* Board & Piece Customization Unlock */}
            <div className="board-customization-banner" data-testid="board-customization-banner">
              <div className="bundle-content">
                <div className="bundle-icon">🎨</div>
                <div className="bundle-info">
                  <h3>Board & Piece Customization</h3>
                  <p>Unlock board themes, piece colors, grid lines & coordinate colors in Settings</p>
                  <div className="bundle-pricing">
                    <span className="bundle-price">${BOARD_CUSTOMIZATION_PRICE.toFixed(2)}</span>
                    <span style={{ fontSize: '12px', color: '#aaa', marginLeft: '4px' }}>one-time</span>
                  </div>
                </div>
                {boardCustomizationUnlocked ? (
                  <div className="subscription-active-badge" style={{ margin: 0 }} data-testid="board-cust-unlocked">
                    <span>Unlocked</span>
                  </div>
                ) : (
                  <button
                    className="bundle-buy-btn"
                    onClick={handlePurchaseBoardCustomization}
                    disabled={boardCustProcessing}
                    data-testid="board-cust-buy-btn"
                  >
                    {boardCustProcessing ? 'Processing...' : 'Unlock $1.99'}
                  </button>
                )}
              </div>
            </div>

            {/* Flame Duke Sets */}
            <div className="flame-sets-section" data-testid="flame-sets-section">
              <h3 className="flame-sets-title">Flame Duke Sets</h3>
              <p className="flame-sets-subtitle">Variant-paired duke skins — buy as a set, equip both!</p>
              <div className="flame-sets-grid">
                {DUKE_SKIN_SETS.map(set => {
                  const allOwned = set.skins.every(s => ownedDukeSkins.includes(s));
                  const isPurchasing = processingPurchase === set.id;
                  return (
                    <div key={set.id} className={`flame-set-card ${set.rarity} ${allOwned ? 'owned' : ''}`} data-testid={`duke-set-${set.id}`}>
                      <div className="flame-set-header">
                        <span className="rarity-tag" style={{ backgroundColor: getRarityColor(set.rarity) }}>{set.rarity}</span>
                        <h4>{set.name}</h4>
                      </div>
                      <div className="flame-set-skins">
                        {set.skins.map(skinId => {
                          const skin = DUKE_SKIN_CATALOG.find(s => s.id === skinId);
                          const label = set.labels[skinId];
                          return (
                            <div key={skinId} className="flame-set-skin-item">
                              <div className="flame-set-skin-images">
                                <img src={getDukeImageUrl(skinId, 'instant_king')} alt={`${skin?.name} IK`} className="duke-skin-img" onError={(e) => { e.target.style.display = 'none'; }} />
                                <img src={getDukeImageUrl(skinId, 'fight_to_be_king')} alt={`${skin?.name} FTK`} className="duke-skin-img" onError={(e) => { e.target.style.display = 'none'; }} />
                              </div>
                              <div className="flame-set-skin-label">
                                <strong>{skin?.name}</strong>
                                <span className="variant-label">{label}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <p className="flame-set-description">{set.description}</p>
                      <div className="flame-set-footer">
                        {allOwned ? (
                          <div className="owned-badge" style={{ position: 'static', padding: '6px 16px' }}>Owned</div>
                        ) : (
                          <>
                            <div className="flame-set-pricing">
                              <span className="original-price">${set.originalPrice.toFixed(2)}</span>
                              <span className="bundle-price">${set.price.toFixed(2)}</span>
                            </div>
                            <button
                              className="buy-btn"
                              onClick={() => handlePurchaseSet(set.id)}
                              disabled={isPurchasing}
                              data-testid={`buy-set-${set.id}`}
                            >
                              {isPurchasing ? 'Processing...' : `Buy Set $${set.price.toFixed(2)}`}
                            </button>
                          </>
                        )}
                        <button
                          className="gift-set-btn"
                          onClick={() => { setGiftModalSet(set); setGiftRecipient(''); setGiftMessage(''); }}
                          data-testid={`gift-set-${set.id}`}
                          title="Gift this set to a friend"
                        >
                          Gift
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Bundle Offer */}
            <div className="bundle-banner">
              <div className="bundle-content">
                <div className="bundle-icon">🎁</div>
                <div className="bundle-info">
                  <h3>All Duke Skins Bundle</h3>
                  <p>40% OFF - Get all {PAID_DUKE_SKINS.length} premium duke skins!</p>
                  <div className="bundle-pricing">
                    <span className="original-price">${DUKE_ORIGINAL_PRICE.toFixed(2)}</span>
                    <span className="bundle-price">${DUKE_BUNDLE_PRICE.toFixed(2)}</span>
                  </div>
                </div>
                <button 
                  className={`bundle-buy-btn ${isPurchaseLocked ? 'locked' : ''}`}
                  onClick={() => handlePurchase('duke-bundle', 'duke-bundle')}
                  disabled={processingPurchase === 'duke-bundle' || isPurchaseLocked}
                >
                  {processingPurchase === 'duke-bundle' ? 'Processing...' : isPurchaseLocked ? 'Locked' : 'Buy Bundle'}
                </button>
              </div>
            </div>

            {/* Category Filter */}
            <div className="duke-store-categories">
              {dukeCategories.map(cat => (
                <button
                  key={cat.id}
                  className={`category-btn ${selectedCategory === cat.id ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(cat.id)}
                >
                  <span className="cat-icon">{cat.icon}</span>
                  <span className="cat-label">{t(cat.labelKey)}</span>
                </button>
              ))}
            </div>

            {/* Duke Skins Grid */}
            <div className="duke-skins-grid">
              {filteredDukeSkins.map(skin => {
                const isOwned = ownedDukeSkins.includes(skin.id);
                const isPurchasing = processingPurchase === skin.id;
                const isEquippedWhite = equippedDukeSkinWhite === skin.id;
                const isEquippedBlack = equippedDukeSkinBlack === skin.id;
                const isEquipped = isEquippedWhite || isEquippedBlack;
                const equippedFor = isEquippedWhite && isEquippedBlack ? 'Both' : 
                                    isEquippedWhite ? 'White' : 
                                    isEquippedBlack ? 'Black' : '';
                const isColorizable = skin.colorizable === true;
                return (
                  <div 
                    key={skin.id} 
                    className={`duke-skin-card ${isOwned ? 'owned' : ''} ${skin.isFree ? 'free' : ''} ${isEquipped ? 'equipped' : ''} ${isColorizable ? 'colorizable-skin' : ''}`}
                  >
                    <div className="skin-preview duke-preview">
                      <div className="duke-skin-images">
                        <img 
                          src={getDukeImageUrl(skin.id, 'instant_king')} 
                          alt={`${skin.name} IK`} 
                          className="duke-skin-img"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <img 
                          src={getDukeImageUrl(skin.id, 'fight_to_be_king')} 
                          alt={`${skin.name} FTK`} 
                          className="duke-skin-img"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                      <span className="rarity-tag" style={{ backgroundColor: getRarityColor(skin.rarity) }}>
                        {skin.rarity}
                      </span>
                      {isColorizable && (
                        <span className="colorizable-badge">
                          Color Adaptive
                        </span>
                      )}
                      {isOwned && <div className="owned-badge">Owned</div>}
                      {isEquipped && <div className="equipped-badge">{equippedFor}</div>}
                    </div>
                    <div className="skin-details">
                      <h4 className="skin-name">{skin.name}</h4>
                      <p className="skin-description">{skin.description}</p>
                      <div className="skin-actions">
                        {skin.isFree ? (
                          <button 
                            className={`equip-btn free ${isEquipped ? 'equipped-btn' : ''}`} 
                            onClick={() => handleEquipDukeSkin(skin.id)}
                          >
                            {isEquipped ? `${equippedFor}` : 'Free'}
                          </button>
                        ) : isOwned ? (
                          <button 
                            className={`equip-btn ${isEquipped ? 'equipped-btn' : ''}`} 
                            onClick={() => handleEquipDukeSkin(skin.id)}
                          >
                            {isEquipped ? `${equippedFor}` : 'Equip'}
                          </button>
                        ) : (
                          <button 
                            className={`buy-btn ${isPurchaseLocked ? 'locked' : ''}`}
                            onClick={() => handlePurchase(skin.id, 'duke')}
                            disabled={isPurchasing || isPurchaseLocked}
                          >
                            {isPurchasing ? 'Processing...' : isPurchaseLocked ? 'Locked' : `$${skin.price.toFixed(2)}`}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <div className="duke-store-footer">
          <p>Secure payment via Stripe - One-time purchase - Instant access</p>
        </div>
      </div>

      {/* Gift Modal */}
      {giftModalSet && (
        <div className="gift-modal-overlay" data-testid="gift-modal-overlay" onClick={() => setGiftModalSet(null)}>
          <div className="gift-modal" data-testid="gift-modal" onClick={(e) => e.stopPropagation()}>
            <button className="gift-modal-close" onClick={() => setGiftModalSet(null)}>X</button>
            <div className="gift-modal-header">
              <div className="gift-modal-icon">🎁</div>
              <h3>Gift {giftModalSet.name}</h3>
              <p className="gift-modal-price">${giftModalSet.price.toFixed(2)}</p>
            </div>
            <div className="gift-modal-body">
              <label className="gift-label">Recipient (username or email)</label>
              <input
                type="text"
                className="gift-input"
                placeholder="Enter username or email..."
                value={giftRecipient}
                onChange={(e) => setGiftRecipient(e.target.value)}
                data-testid="gift-recipient-input"
              />
              <label className="gift-label">Message (optional)</label>
              <textarea
                className="gift-textarea"
                placeholder="Add a personal message..."
                value={giftMessage}
                onChange={(e) => setGiftMessage(e.target.value)}
                rows={3}
                maxLength={200}
                data-testid="gift-message-input"
              />
            </div>
            <div className="gift-modal-footer">
              <button className="gift-cancel-btn" onClick={() => setGiftModalSet(null)}>Cancel</button>
              <button
                className="gift-send-btn"
                onClick={handleGiftSet}
                disabled={giftProcessing || !giftRecipient.trim()}
                data-testid="gift-send-btn"
              >
                {giftProcessing ? 'Sending...' : `Send Gift`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkinStore;
